type MessageHandler = (message: string) => void;
type StatusHandler = (connected: boolean) => void;

export interface WebSocketClientOptions {
  url: string;
  reconnectIntervalMs?: number;
  debug?: boolean;
}

export default class WebSocketClient {

  private socket?: WebSocket;
  private url: string;
  private reconnectIntervalMs: number;
  private debug: boolean;

  private messageHandlers: MessageHandler[] = [];
  private statusHandlers: StatusHandler[] = [];

  private reconnectTimer?: number;
  private manuallyClosed = false;

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.reconnectIntervalMs = options.reconnectIntervalMs ?? 3000;
    this.debug = options.debug ?? false;
  }

  // -------------------------
  // Connection
  // -------------------------

  connect() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    this.manuallyClosed = false;

    this.log("Connecting to", this.url);
    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      this.log("Connected");
      this.notifyStatus(true);
    };

    this.socket.onmessage = (event) => {
      this.log("Message received:", event.data);
      this.notifyMessage(event.data);
    };

    this.socket.onerror = (err) => {
      this.log("Socket error", err);
    };

    this.socket.onclose = () => {
      this.log("Disconnected");
      this.notifyStatus(false);

      if (!this.manuallyClosed) {
        this.scheduleReconnect();
      }
    };
  }

  disconnect() {
    this.manuallyClosed = true;
    this.clearReconnect();
    this.socket?.close();
    this.socket = undefined;
  }

  // -------------------------
  // Messaging
  // -------------------------

  send(message: string | object) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.log("Socket not connected");
      return;
    }

    const payload =
      typeof message === "string"
        ? message
        : JSON.stringify(message);

    this.log("Sending:", payload);
    this.socket.send(payload);
  }

  // -------------------------
  // Events
  // -------------------------

  onMessage(handler: MessageHandler) {
    this.messageHandlers.push(handler);
  }

  onStatusChange(handler: StatusHandler) {
    this.statusHandlers.push(handler);
  }

  // -------------------------
  // Internal helpers
  // -------------------------

  private scheduleReconnect() {
    this.clearReconnect();

    this.log(`Reconnecting in ${this.reconnectIntervalMs} ms...`);
    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, this.reconnectIntervalMs);
  }

  private clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private notifyMessage(message: string) {
    this.messageHandlers.forEach(handler => handler(message));
  }

  private notifyStatus(connected: boolean) {
    this.statusHandlers.forEach(handler => handler(connected));
  }

  private log(...args: any[]) {
    if (this.debug) {
      console.log("[WebSocketClient]", ...args);
    }
  }
}
